import { NormalFormData, ProductType } from './sharedTypes'

/**
 * Pure pricing helpers for the inscription recap / funding summaries.
 * Keep UI components thin: all remise / majoration rules live here so they stay testable.
 */

export type CommercialPricingInput = {
  personalFunding: number | null
  defaultPersonalFunding: number | null | undefined
  listingPrice: number
}

export type CommercialPricing = {
  /** Amount the prospect actually pays (RAC / total perso). */
  amountToPay: number
  /**
   * personal - default.
   * < 0 → real commercial discount (show "Remise commerciale")
   * > 0 → surcharge / inverted discount (never show as remise)
   * = 0 → no difference
   */
  commercialDiscount: number
  /** True only for a real discount (client pays less than default). */
  hasCommercialDiscount: boolean
  /** True when client pays more than the default / listing baseline. */
  hasSurcharge: boolean
  /**
   * Formation price shown in the recap.
   * On surcharge: the charged amount (keeps totals coherent without a remise line).
   * Otherwise: catalog / listing price.
   */
  displayedFormationPrice: number
}

/**
 * Resolve what the prospect owes from personal funding fields, falling back to listing.
 */
export const resolveAmountToPay = ({
  personalFunding,
  defaultPersonalFunding,
  listingPrice,
}: CommercialPricingInput): number => {
  return personalFunding ?? defaultPersonalFunding ?? listingPrice
}

/**
 * Difference personal - default.
 * Returns 0 when personal is null or default is unset (null/undefined).
 * `default === 0` is a valid baseline (Bugbot: do not use truthiness).
 */
export const computeCommercialDiscount = (
  personalFunding: number | null,
  defaultPersonalFunding: number | null | undefined,
): number => {
  if (personalFunding === null || defaultPersonalFunding == null) {
    return 0
  }
  return personalFunding - defaultPersonalFunding
}

export const hasCommercialDiscount = (
  personalFunding: number | null,
  defaultPersonalFunding: number | null | undefined,
): boolean => {
  return defaultPersonalFunding != null && computeCommercialDiscount(personalFunding, defaultPersonalFunding) < 0
}

export const hasSurcharge = (
  personalFunding: number | null,
  defaultPersonalFunding: number | null | undefined,
): boolean => {
  return computeCommercialDiscount(personalFunding, defaultPersonalFunding) > 0
}

/**
 * Alma / Bank transfer: on surcharge show amountToPay, else listing.
 */
export const resolveDisplayedFormationPrice = (input: CommercialPricingInput): number => {
  const amountToPay = resolveAmountToPay(input)
  if (hasSurcharge(input.personalFunding, input.defaultPersonalFunding)) {
    return amountToPay
  }
  return input.listingPrice
}

/**
 * DPC: on surcharge the formation total is RAC + ANDPC coverage.
 */
export const resolveDisplayedDpcFormationPrice = (
  input: CommercialPricingInput & { totalPartOrga: number },
): number => {
  const personal =
    input.personalFunding !== null ? input.personalFunding : input.defaultPersonalFunding ?? 0
  if (hasSurcharge(input.personalFunding, input.defaultPersonalFunding)) {
    return personal + input.totalPartOrga
  }
  return input.listingPrice
}

export const resolveCommercialPricing = (input: CommercialPricingInput): CommercialPricing => {
  const amountToPay = resolveAmountToPay(input)
  const commercialDiscount = computeCommercialDiscount(input.personalFunding, input.defaultPersonalFunding)
  return {
    amountToPay,
    commercialDiscount,
    hasCommercialDiscount: hasCommercialDiscount(input.personalFunding, input.defaultPersonalFunding),
    hasSurcharge: commercialDiscount > 0,
    displayedFormationPrice: resolveDisplayedFormationPrice(input),
  }
}

/**
 * Scale a product line so the detail accordion sums to the formation price shown in the recap
 * (required on surcharge when FORs are missing or still at catalog price).
 */
export const scaleProductPriceForSurcharge = (
  basePrice: number,
  baseTotal: number,
  targetTotal: number,
  surcharge: boolean,
): number => {
  if (!surcharge || baseTotal <= 0) {
    return basePrice
  }
  return basePrice * (targetTotal / baseTotal)
}

/**
 * DPC pré-inscription: "Remise commerciale" is listing - part ORGA, only when positive.
 */
export const resolveDpcPreRegistrationRemise = (listingPrice: number, totalPartOrga: number): number | null => {
  const delta = listingPrice - totalPartOrga
  return delta > 0 ? delta : null
}

/**
 * OPCO: show remise only when personal RAC is below theoretical RAC (estimate - opco).
 */
export const resolveOpcoRemise = (
  estimateAmount: number,
  opcoAmount: number,
  personalFunding: number,
): number | null => {
  const theoreticalRac = estimateAmount - opcoAmount
  if (personalFunding < theoreticalRac) {
    return theoreticalRac - personalFunding
  }
  return null
}

/**
 * Catalog vs charged listing (generic funding summary strikethrough).
 */
export const hasCatalogDiscount = (catalogPrice: number, chargedPrice: number): boolean => {
  return catalogPrice > 0 && catalogPrice > chargedPrice
}

/**
 * DPC net compensation display after optional RAC deduction.
 */
export const resolveDpcNetCompensation = (totalCompensation: number, personalFunding: number): number => {
  if (totalCompensation - personalFunding > 0) {
    return totalCompensation - personalFunding
  }
  return totalCompensation
}

export const sumProductListingPrices = (products: { listing_price: number }[]): number => {
  return products.reduce((acc, p) => acc + p.listing_price, 0)
}

export type FundingOrganismRegistrationPrice = {
  cancelled: boolean
  product_listing_price: number
  product: { product_code: string; listing_price?: number }
  part_orga?: string | null
  compensation?: string | null
}

export const sumActiveProductListingPrices = (registrations: FundingOrganismRegistrationPrice[]): number => {
  return registrations.filter((r) => !r.cancelled).reduce((acc, r) => acc + r.product_listing_price, 0)
}

/**
 * Prod case regression: BANK_TRANSFER multi-product with personal > catalog sum.
 * Ensures detail lines sum (within float tolerance) to amountToPay when scaled.
 */
export const buildScaledProductPrices = (
  products: { product_code: string; listing_price: number }[],
  registrations: FundingOrganismRegistrationPrice[],
  personalFunding: number | null,
  defaultPersonalFunding: number | null | undefined,
): { product_code: string; catalogPrice: number; actualPrice: number; productHasDiscount: boolean }[] => {
  const catalogTotal = sumProductListingPrices(products)
  const amountToPay = resolveAmountToPay({
    personalFunding,
    defaultPersonalFunding,
    listingPrice: catalogTotal,
  })
  const surcharge = hasSurcharge(personalFunding, defaultPersonalFunding)

  const basePrices = products.flatMap((product) => {
    const registration = registrations.find((r) => r.product.product_code === product.product_code && !r.cancelled)
    // Produit uniquement annulé : ne pas réafficher au prix catalogue.
    const hasCancelledOnly =
      !registration && registrations.some((r) => r.product.product_code === product.product_code && r.cancelled)
    if (hasCancelledOnly) return []

    return [
      {
        product_code: product.product_code,
        catalogPrice: product.listing_price,
        basePrice: registration?.product_listing_price ?? product.listing_price,
      },
    ]
  })
  const baseTotal = basePrices.reduce((acc, p) => acc + p.basePrice, 0)

  return basePrices.map(({ product_code, catalogPrice, basePrice }) => {
    const actualPrice = scaleProductPriceForSurcharge(basePrice, baseTotal, amountToPay, surcharge)
    return {
      product_code,
      catalogPrice,
      actualPrice,
      productHasDiscount: hasCatalogDiscount(catalogPrice, actualPrice),
    }
  })
}


type FundingOrganismRegistration = NormalFormData['funding_organism_registrations'][number]

export const getActiveFundingOrganismRegistrations = (data: NormalFormData): FundingOrganismRegistration[] =>
  data.funding_organism_registrations.filter((r) => !r.cancelled)

/** Un parcours "multi-produits" est une Registration qui porte plusieurs formations. */
export const hasMultipleProducts = (data: NormalFormData): boolean =>
  data.products.length > 0 && data.product === null

/** Somme brute des `product_listing_price` des inscriptions actives (sans ajustement de majoration). */
export const getActiveRegistrationsListingPriceSum = (data: NormalFormData): number =>
  getActiveFundingOrganismRegistrations(data).reduce((acc, r) => acc + r.product_listing_price, 0)

export const getTotalPartOrga = (data: NormalFormData): number =>
  getActiveFundingOrganismRegistrations(data).reduce((acc, r) => acc + Number(r.part_orga), 0)

export const getTotalCompensation = (data: NormalFormData): number =>
  getActiveFundingOrganismRegistrations(data).reduce((acc, r) => acc + Number(r.compensation), 0)

/** Reste à charge personnel dû, en tenant compte d'un éventuel montant par défaut du financeur. */
export const getPersonalFundingDue = (data: NormalFormData): number =>
  data.personal_funding !== null ? data.personal_funding : (data.default_personal_funding ?? 0)

/**
 * Écart entre le reste à charge personnel réellement demandé et celui par défaut.
 * Négatif => vraie remise commerciale ; positif => majoration (ne doit jamais être
 * affiché comme une remise).
 */
export const getCommercialDiscount = (data: NormalFormData): number =>
  data.personal_funding !== null && data.default_personal_funding != null
    ? data.personal_funding - data.default_personal_funding
    : 0

/** `getCommercialDiscount` < 0 correspond à une vraie remise commerciale (pas une majoration). */
export const hasRealCommercialDiscount = (data: NormalFormData): boolean =>
  data.default_personal_funding != null && getCommercialDiscount(data) < 0

/**
 * Prix réellement facturé (post-remise), agrégé sur toutes les inscriptions actives
 * en cas de parcours multi-produits, ou prix du produit unique sinon.
 *
 * Fait foi : la version avec fallback via `funding_organism_registrations[0]` (issue de
 * l'ancien FundingSummary.tsx), plus complète que l'ancienne version de Recap.tsx qui
 * retombait directement à 0 — divergence corrigée explicitement ici, cf. plan.
 */
export const getListingPrice = (data: NormalFormData): number => {
  if (hasMultipleProducts(data)) {
    return getActiveRegistrationsListingPriceSum(data)
  }
  return data.funding_organism_registrations[0]?.product_listing_price ?? data.product?.listing_price ?? 0
}

/** Prix catalogue (liste, avant toute remise). */
export const getCatalogPrice = (data: NormalFormData): number => {
  if (hasMultipleProducts(data)) {
    return getActiveFundingOrganismRegistrations(data).reduce((acc, r) => acc + r.product.listing_price, 0)
  }
  return data.product_catalog?.listing_price ?? data.product?.listing_price ?? 0
}

export const hasPriceDiscount = (data: NormalFormData): boolean => {
  const catalogPrice = getCatalogPrice(data)
  return catalogPrice > 0 && catalogPrice > getListingPrice(data)
}

/**
 * Prix "formation" affiché en tête du résumé DPC, qui doit rester cohérent avec le
 * montant réellement facturé en cas de majoration commerciale (cf. FundingSummaryDPC.tsx).
 */
export const getDpcDisplayedFormationPrice = (data: NormalFormData): number => {
  const commercialDiscount = getCommercialDiscount(data)
  return commercialDiscount > 0
    ? getPersonalFundingDue(data) + getTotalPartOrga(data)
    : getActiveRegistrationsListingPriceSum(data)
}

// --- Détail par produit (accordéon "Voir le détail de vos inscriptions") ---

/**
 * Prix d'un produit donné dans un parcours DPC multi-produits, proportionné pour
 * rester cohérent avec le montant total facturé en cas de majoration commerciale.
 */
export const getDpcProductPrice = (data: NormalFormData, product: ProductType): number => {
  const activeRegs = getActiveFundingOrganismRegistrations(data)
  const registration = activeRegs.find((r) => r.product.product_code === product.product_code)
  const productCatalog = data.product_catalogs.find(
    (pc) => pc.product === product.id && pc.funding_organism === data.funding_organism?.code,
  )

  let productPrice = registration?.product_listing_price || productCatalog?.listing_price || product.listing_price

  const commercialDiscount = getCommercialDiscount(data)
  const listingTotal = activeRegs.reduce((acc, r) => acc + r.product_listing_price, 0)
  if (commercialDiscount > 0 && listingTotal > 0) {
    const targetFormationPrice = getPersonalFundingDue(data) + getTotalPartOrga(data)
    productPrice = productPrice * (targetFormationPrice / listingTotal)
  }
  return productPrice
}

/** Montant pris en charge par l'ANDPC pour un produit donné d'un parcours DPC multi-produits. */
export const getDpcProductPartOrga = (data: NormalFormData, product: ProductType): number => {
  const registration = getActiveFundingOrganismRegistrations(data).find(
    (r) => r.product.product_code === product.product_code,
  )
  const productCatalog = data.product_catalogs.find(
    (pc) => pc.product === product.id && pc.funding_organism === data.funding_organism?.code,
  )
  return Number(registration?.part_orga || productCatalog?.funding_price || product.listing_price)
}

/** Prix d'un produit donné dans un parcours OPCO multi-produits, lu depuis le devis. */
export const getOpcoProductPrice = (data: NormalFormData, product: ProductType): number => {
  const lineItem = data.estimate?.line_items.find((li) => li.product_id === product.id)
  return (lineItem?.quantity || 1) * (lineItem?.unit_price || 0)
}

/**
 * Prix d'un produit donné dans un parcours multi-produits générique (ALMA, Stripe,
 * virement...), proportionné pour rester cohérent avec le montant total facturé en cas
 * de majoration commerciale. Retourne `null` si le produit est annulé et ne doit pas
 * être réaffiché.
 */
export const getGenericProductPrice = (data: NormalFormData, product: ProductType): number | null => {
  const activeRegs = getActiveFundingOrganismRegistrations(data)
  const registration = activeRegs.find((r) => r.product.product_code === product.product_code)
  // Produit uniquement annulé : ne pas réafficher au prix catalogue.
  // Absence totale de registration (BANK_TRANSFER / ALMA multi-produits) : fallback catalogue OK.
  const hasCancelledOnly =
    !registration &&
    data.funding_organism_registrations.some((r) => r.product.product_code === product.product_code && r.cancelled)
  if (hasCancelledOnly) return null

  const catalogProductPrice = product.listing_price
  const basePrice = registration?.product_listing_price ?? catalogProductPrice

  const commercialDiscount = getCommercialDiscount(data)
  let actualPrice = basePrice
  if (commercialDiscount > 0) {
    const catalogTotal = data.products.reduce((acc, p) => acc + p.listing_price, 0)
    const amountToPay = data.personal_funding ?? data.default_personal_funding ?? catalogTotal
    const baseTotal = data.products.reduce((acc, p) => {
      const reg = activeRegs.find((r) => r.product.product_code === p.product_code)
      const isCancelledOnly =
        !reg &&
        data.funding_organism_registrations.some((r) => r.product.product_code === p.product_code && r.cancelled)
      if (isCancelledOnly) return acc
      return acc + (reg?.product_listing_price ?? p.listing_price)
    }, 0)
    if (baseTotal > 0) {
      actualPrice = basePrice * (amountToPay / baseTotal)
    }
  }
  return actualPrice
}
